import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ParentCarnet } from "../components/samourais/ParentCarnet";
import { PUBLIC_ORIGIN } from "../lib/domain";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const card = "rounded-2xl border border-white/10 bg-white/[0.04] p-4";
const inp = "rounded-xl bg-zinc-900 border border-white/10 p-2.5 text-sm w-full";
const fmt = (d) => (d ? new Date(d + (d.length === 10 ? "T12:00:00" : "")).toLocaleDateString("fr-CA", { weekday: "short", day: "numeric", month: "short" }) : "—");

export default function EspaceParentSamourais() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState(null);
  const [help, setHelp] = useState({ topic: "horaire", message: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/samourais/public/parent/${token}`); setData(r.data); }
    catch (e) { setErr(e.response?.data?.detail || "Lien invalide"); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/samourais/public/parent/${token}`, edit);
      toast.success(r.data.changed?.length ? "Coordonnées mises à jour" : "Aucun changement");
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  const sendHelp = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/samourais/public/parent/${token}/help`, help);
      toast.success("Demande envoyée — le dojo vous rappelle rapidement 🙏"); setHelp({ topic: "horaire", message: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  if (err) return <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 text-center" data-testid="parent-space-error">{err}</div>;
  if (!data) return <div className="min-h-screen bg-black text-white flex items-center justify-center">Chargement…</div>;
  const { child, parent, payment, session, attendance, certificates, messages, contract } = data;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="parent-space">
      <div className="max-w-2xl mx-auto px-5 py-8 space-y-4">
        <header>
          <div className="text-[10px] tracking-[0.35em] text-red-400">PETITS SAMOURAÏS · ESPACE PARENT</div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1" data-testid="parent-space-child">🥋 {child.name}{child.age ? ` · ${child.age} ans` : ""}</h1>
          <p className="text-zinc-400 text-sm">Bonjour {parent.name} — {data.formule_label}</p>
        </header>

        <section className={`${card} flex flex-wrap items-center justify-between gap-3`} data-testid="parent-payment">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-400">Paiement</div>
            <div className={`text-lg font-semibold ${payment.paid ? "text-emerald-300" : "text-amber-300"}`}>{payment.label}</div>
            <div className="text-xs text-zinc-400">{payment.amount} $ {payment.paid_at ? `· reçu le ${payment.paid_at.slice(0, 10)}` : ""}</div>
          </div>
          <div className="text-right text-xs text-zinc-400">
            <div>Contrat : {contract.signed ? <span className="text-emerald-300">signé ✓</span> : "à signer au dojo"}</div>
            {!payment.paid && <div className="text-amber-300 mt-1">Si vous avez déjà payé, cliquez « J'ai besoin d'aide » — on vérifie.</div>}
            <a href={`${PUBLIC_ORIGIN}/inscription?programme=parent_enfant`} target="_blank" rel="noreferrer" data-testid="parent-inscription-link"
               className={`mt-2 inline-block rounded-full px-4 py-1.5 text-sm font-semibold ${payment.paid ? "border border-zinc-600 text-zinc-200 hover:bg-zinc-800" : "bg-amber-400 text-black hover:bg-amber-300"}`}>
              {payment.paid ? "Inscription Petits Samouraïs Parent-Enfant" : "Payer / compléter mon inscription"}
            </a>
          </div>
        </section>

        <section className={card} data-testid="parent-schedule">
          <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Horaire</div>
          <div className="font-semibold">{session.title}</div>
          <div className="text-sm text-zinc-300">{session.days} · {session.time} · {session.weeks} semaines à partir du {session.start_date}</div>
          {session.next_date && <div className="mt-2 inline-block rounded-full bg-red-500/15 border border-red-500/40 px-3 py-1 text-sm" data-testid="parent-next-class">Prochain cours : <b>{fmt(session.next_date)}</b></div>}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {session.dates.map((d) => {
              const a = attendance.find((x) => x.date === d);
              return <span key={d} className={`text-[11px] rounded-md px-2 py-1 border ${a ? (a.present ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-red-500/40 bg-red-500/10 text-red-200") : "border-white/10 text-zinc-400"}`} title={a ? (a.present ? "Présent" : "Absent") : "À venir"}>{fmt(d)}{a ? (a.present ? " ✓" : " ✕") : ""}</span>;
            })}
          </div>
        </section>

        <section className={card} data-testid="parent-attendance">
          <div className="flex justify-between items-center">
            <div className="text-xs uppercase tracking-widest text-zinc-400">Présences</div>
            {data.attendance_rate !== null && <span className="text-sm font-semibold">{data.attendance_rate} % d'assiduité</span>}
          </div>
          <div className="text-sm text-zinc-300 mt-1">{attendance.filter((a) => a.present).length} présence(s) · {attendance.filter((a) => !a.present).length} absence(s)</div>
          {certificates.length > 0 && <div className="mt-2 text-sm">🏅 Certificats : {certificates.map((c) => c.label || c.type).join(", ")}</div>}
        </section>

        <ParentCarnet carnet={data.carnet} token={token} />

        <section className={card} data-testid="parent-equipment">
          <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Équipement</div>
          <p className="text-sm text-zinc-300">Aucun achat requis pour l'essai. Réservez les bonnes tailles (pantalon, t-shirt, petits gants) pour la commande groupée du bloc.</p>
          <a href="/petits-samourais/equipement" target="_blank" rel="noreferrer" className="inline-block mt-2 text-sm text-amber-300 underline" data-testid="parent-equipment-link">🥋 Tailles, photos et réservation →</a>
        </section>

        <section className={card} data-testid="parent-contact">
          <div className="flex justify-between items-center mb-2">
            <div className="text-xs uppercase tracking-widest text-zinc-400">Mes coordonnées</div>
            {!edit && <button onClick={() => setEdit({ parent_phone: parent.phone || "", parent_email: parent.email || "", contacts_urgence: data.contacts_urgence.length ? data.contacts_urgence : [{ nom: "", telephone: "", lien: "" }] })} className="text-xs text-sky-300 underline" data-testid="parent-edit-btn">Modifier</button>}
          </div>
          {!edit ? (
            <div className="text-sm text-zinc-300">
              <div>{parent.email} · {parent.phone || "téléphone manquant"}</div>
              <div className="text-xs text-zinc-400 mt-1">Urgence : {data.contacts_urgence.map((c) => `${c.nom} (${c.telephone})`).join(", ") || "aucun contact — merci d'en ajouter"}</div>
            </div>
          ) : (
            <div className="space-y-2">
              <input className={inp} value={edit.parent_email} onChange={(e) => setEdit({ ...edit, parent_email: e.target.value })} placeholder="Courriel" data-testid="parent-edit-email" />
              <input className={inp} value={edit.parent_phone} onChange={(e) => setEdit({ ...edit, parent_phone: e.target.value })} placeholder="Téléphone" data-testid="parent-edit-phone" />
              {edit.contacts_urgence.map((c, i) => (
                <div key={i} className="grid grid-cols-3 gap-2">
                  <input className={inp} value={c.nom} onChange={(e) => setEdit({ ...edit, contacts_urgence: edit.contacts_urgence.map((x, j) => j === i ? { ...x, nom: e.target.value } : x) })} placeholder="Contact urgence" data-testid={`parent-edit-urg-nom-${i}`} />
                  <input className={inp} value={c.telephone} onChange={(e) => setEdit({ ...edit, contacts_urgence: edit.contacts_urgence.map((x, j) => j === i ? { ...x, telephone: e.target.value } : x) })} placeholder="Téléphone" data-testid={`parent-edit-urg-tel-${i}`} />
                  <input className={inp} value={c.lien || ""} onChange={(e) => setEdit({ ...edit, contacts_urgence: edit.contacts_urgence.map((x, j) => j === i ? { ...x, lien: e.target.value } : x) })} placeholder="Lien" />
                </div>
              ))}
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEdit(null)} className="text-xs border border-white/15 rounded-lg px-3 py-1.5">Annuler</button>
                <button onClick={saveEdit} disabled={busy} className="text-xs bg-red-600 rounded-lg px-3 py-1.5 font-semibold" data-testid="parent-edit-save">Enregistrer</button>
              </div>
            </div>
          )}
        </section>

        <section className={card} data-testid="parent-messages">
          <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Messages du dojo</div>
          {messages.length === 0 && <p className="text-sm text-zinc-500">Aucun message pour l'instant.</p>}
          <ul className="space-y-2">
            {messages.slice(0, 8).map((m, i) => (
              <li key={i} className="text-sm rounded-lg bg-black/30 p-2.5">
                <div className="text-[11px] text-zinc-500">{m.at?.slice(0, 10)} · {m.kind}</div>
                <div>{m.subject || m.message}</div>
              </li>
            ))}
          </ul>
        </section>

        <section className={`${card} border-red-500/40`} data-testid="parent-help">
          <div className="text-xs uppercase tracking-widest text-red-300 mb-2">🆘 J'ai besoin d'aide</div>
          <p className="text-xs text-zinc-400 mb-2">Horaire, paiement, équipement, absence… Votre message crée une tâche pour l'équipe du dojo, qui vous rappelle.</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <select className={inp} value={help.topic} onChange={(e) => setHelp({ ...help, topic: e.target.value })} data-testid="parent-help-topic">
              <option value="horaire">Horaire</option><option value="paiement">Paiement</option><option value="equipement">Équipement</option><option value="absence">Absence</option><option value="autre">Autre</option>
            </select>
            <textarea className={`${inp} sm:col-span-2 min-h-[60px]`} value={help.message} onChange={(e) => setHelp({ ...help, message: e.target.value })} placeholder="Décrivez votre besoin…" data-testid="parent-help-message" />
          </div>
          <button onClick={sendHelp} disabled={busy || help.message.trim().length < 3} className="mt-2 w-full rounded-xl bg-red-600 hover:bg-red-500 py-3 font-semibold disabled:opacity-50" data-testid="parent-help-send">Envoyer ma demande</button>
          {data.help_requests.length > 0 && <p className="text-[11px] text-zinc-500 mt-2">{data.help_requests.length} demande(s) envoyée(s) · dernière le {data.help_requests[data.help_requests.length - 1].at?.slice(0, 10)}</p>}
        </section>
        <p className="text-center text-xs text-zinc-600">Institut Impact Élite Martial · (819) 527-9446 · lien personnel, ne pas partager</p>
      </div>
    </div>
  );
}
